import { NavHeader } from '@/components/features/shared/NavHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { UserRole } from '@/domain/auth/auth'
import { OfferingStatus, formatOfferingStatus } from '@/domain/offerings/offering'
import { useDeleteOffering, useOffering } from '@/hooks/api/useOfferings'
import { Paths } from '@/routes/paths'
import { useAuthStore } from '@/store/useAuthStore'
import { useNavigate } from '@tanstack/react-router'
import { formatDate } from 'date-fns'
import { es } from 'date-fns/locale'
import { ArrowLeft, Edit, Loader2, Package, Trash2 } from 'lucide-react'
import { serviceTypeBadge } from '../../components/features/offerings/common'
import { OfferingDetailsCard } from '../../components/features/offerings/OfferingDetailsCard'
import { OfferingPricesCard } from '../../components/features/offerings/OfferingPricesCard'
import { OfferingAgePricingCard } from '../../components/features/offerings/OfferingAgePricingCard'
import { PageContainer } from '@/components/features/shared/PageContainer'

interface SingleOfferingPageProps {
  offeringId: string
}

export const SingleOfferingPage = ({ offeringId }: SingleOfferingPageProps) => {
  const navigate = useNavigate()
  const { hasAnyRole } = useAuthStore()
  const { data: offering, isLoading, error, refetch } = useOffering(offeringId, true)
  const { mutateAsync: deleteOffering, isPending: isDeleting } = useDeleteOffering()

  const canManage = hasAnyRole([UserRole.Admin, UserRole.SuperAdmin])

  // console.log(offering)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (error || !offering) {
    return (
      <div className="text-center py-8">
        <Package className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">No se pudo cargar el servicio</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate({ to: `${Paths.Management.Offerings}` })}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver al listado
        </Button>
      </div>
    )
  }

  return (
    <PageContainer>
      <NavHeader
        actions={canManage && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate({ to: `${Paths.Management.EditOffering}/${offeringId}` })}
            >
              <Edit className="mr-2 h-4 w-4" />
              Editar
            </Button>
            <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700"
              onClick={() => deleteOffering(offeringId)} disabled={isDeleting}>
              <Trash2 className="mr-2 h-4 w-4" />
              Eliminar
            </Button>
          </>
        )}
      >
        <div className="flex flex-col items-start gap-2">
          <div className="flex items-center gap-2">
            {serviceTypeBadge(offering.serviceType)}
            {offering.isAdditional && <Badge variant="secondary" className="bg-purple-100 text-purple-700">Servicio Adicional</Badge>}
          </div>
          <h1 className="text-3xl font-bold tracking-tight">{offering.name}</h1>
        </div>
      </NavHeader>

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2 space-y-6">
          <OfferingDetailsCard offering={offering} />
          <OfferingPricesCard offering={offering} />
          {offering.hasAgePricing && (
            <OfferingAgePricingCard offering={offering} canManage={canManage} />
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Resumen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground font-bold leading-none mb-1">Estado</span>
                <Badge
                  variant="secondary"
                  className={`w-fit font-bold ${offering.status === OfferingStatus.ACTIVE ? 'bg-green-100 text-green-700 hover:bg-green-100' :
                      offering.status === OfferingStatus.INACTIVE ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' :
                        'bg-red-100 text-red-700 hover:bg-red-100'
                    }`}
                >
                  {formatOfferingStatus(offering.status as OfferingStatus)}
                </Badge>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground font-bold leading-none mb-1">Creado</span>
                <span className='capitalize font-medium'>
                  {offering.createdAt
                    ? formatDate(new Date(offering.createdAt), 'dd MMMM yyyy', { locale: es })
                    : 'N/A'}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground font-bold leading-none mb-1">Última actualización</span>
                <span className='capitalize font-medium'>
                  {offering.updatedAt
                    ? formatDate(new Date(offering.updatedAt), 'dd MMMM yyyy', { locale: es })
                    : 'N/A'}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Facturación</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground font-bold leading-none mb-1">Tipo de facturación por defecto</span>
                <Badge variant={offering.isInvoiceable ? 'default' : 'outline'} className="w-fit">
                  {offering.isInvoiceable ? 'Factura A o B' : 'Recibo X'}
                </Badge>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground font-bold leading-none mb-1">Sujeto a impuestos</span>
                <Badge variant={offering.isTaxable ? 'default' : 'outline'} className="w-fit">
                  {offering.isTaxable ? 'Sí' : 'No'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </PageContainer>
  )
}
